import { grabTargets } from "./grab-move.ts";
import { fullySelectedSegments } from "./lasso.ts";
import type { SelectAllStep } from "./selection.ts";

export type DeleteMenuChoice = "points" | "lines" | "pointsAndLines";

/**
 * Points removed by Delete Points: the selected points, plus both ends of
 * each selected line. Each index once, ascending.
 */
export function pointsForDelete(
  pointCount: number,
  selection: readonly number[],
  selectedLines: readonly number[],
  segments: ReadonlyArray<readonly [number, number]>,
): number[] {
  return grabTargets(pointCount, selection, selectedLines, segments);
}

/**
 * Lines removed by Delete Lines Only. The points stay.
 * Selected lines are removed as they are. When the selection is only points,
 * a line is removed when both of its endpoints are selected.
 */
export function linesForDelete(
  segments: ReadonlyArray<readonly [number, number]>,
  selection: readonly number[],
  selectedLines: readonly number[],
  selectAll?: SelectAllStep | null,
): number[] {
  if (selectAll === "points") return [];
  const explicit: number[] = [];
  const seen = new Set<number>();
  for (const index of selectedLines) {
    if (!Number.isInteger(index) || index < 0 || index >= segments.length || seen.has(index)) continue;
    seen.add(index);
    explicit.push(index);
  }
  if (explicit.length > 0) return explicit;
  return fullySelectedSegments(segments, selection);
}

/** An item is enabled when confirming it would delete something. */
export function deleteMenuEnabled(
  pointCount: number,
  segments: ReadonlyArray<readonly [number, number]>,
  selection: readonly number[],
  selectedLines: readonly number[],
  selectAll?: SelectAllStep | null,
): { points: boolean; lines: boolean; pointsAndLines: boolean } {
  const points = pointsForDelete(pointCount, selection, selectedLines, segments).length > 0;
  return {
    points,
    lines: linesForDelete(segments, selection, selectedLines, selectAll).length > 0,
    pointsAndLines: points,
  };
}
